import { Inject, Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { MSG_CLOSE_WINDOW_DEFAULT } from '@platform-frontend/checkout';
import {
  CloseWindow,
  GotoError,
  InvalidStateError,
  NavigateByUrl,
  SetGuardsActivated,
  ShowErrorNotification,
  SimpleRouterNavigationExtras,
  SimpleRouterState,
  WINDOW
} from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AutostartService } from '../autostart.service';
import { CheckoutVendorType } from '../model/checkout-vendor-type';
import { IIntegrationService } from '../web-checkout-integration.service';
import { WebCheckoutStartParamsSanitizerService } from '../web-checkout-start-params-sanitizer.service';
import { WebCheckoutStartupRedirectionService } from '../web-checkout-startup-redirection.service';
import {
  AUTOSTART_REF_QUERY_PARAM,
  SIGN_IN_QUERY_PARAM,
  START_ACADEMIC_REGISTRATION_QUERY_PARAM,
  START_CHECKOUT_QUERY_PARAM
} from './checkout-standalone-query-params';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class CheckoutStandaloneService implements IIntegrationService {
  constructor(
    private actions$: Actions,
    private autostartService: AutostartService,
    private store: Store,
    private webCheckoutStartParamsSanitizerService: WebCheckoutStartParamsSanitizerService,
    private webCheckoutStartupRedirectionService: WebCheckoutStartupRedirectionService,
    @Inject(WINDOW) private window: Window
  ) {}

  private vendorType: CheckoutVendorType;

  init(): void {
    this.vendorType = this.getCheckoutVendorType(this.window.location.href);
    this.closeFrameOnCloseWindow();
  }

  private closeFrameOnCloseWindow(): void {
    this.actions$
      .pipe(
        ofActionDispatched(CloseWindow),
        tap(() => this.closeFrame())
      )
      .subscribe();
  }

  private closeFrame(): void {
    const redirectUrl = this.webCheckoutStartupRedirectionService.getCloseWindowRedirectUrl(this.store.selectSnapshot(SimpleRouterState.data).isSuccessPage);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      this.store.dispatch([
        new ShowErrorNotification(MSG_CLOSE_WINDOW_DEFAULT),
        new GotoError({ clearNotifications: false, queryParams: { errorType: 'WINDOW_COULD_NOT_BE_CLOSED' } })
      ]);
    }
  }

  isApplicationVisible(): Observable<boolean> {
    return of(true);
  }

  isRestartingOrShuttingDown(): Observable<boolean> {
    return of(false);
  }

  restart(restartUrl?: string | undefined): Observable<unknown> {
    return this.store.dispatch(restartUrl ? new NavigateByUrl(restartUrl, { replaceUrl: true }) : new CloseWindow());
  }

  handleStartupQueryParams(queryParamMap: ParamMap): Observable<unknown> {
    const autostartRefQueryParam = queryParamMap.get(AUTOSTART_REF_QUERY_PARAM);
    const startCheckoutQueryParam = queryParamMap.get(START_CHECKOUT_QUERY_PARAM);
    const signInQueryParam = queryParamMap.get(SIGN_IN_QUERY_PARAM);
    const startAcademicRegistrationQueryParam = queryParamMap.get(START_ACADEMIC_REGISTRATION_QUERY_PARAM);
    const extras: SimpleRouterNavigationExtras = { replaceUrl: true };

    if (autostartRefQueryParam) {
      return this.autostartService.performAutostart(autostartRefQueryParam);
    } else if (startCheckoutQueryParam) {
      const startCheckoutParams = this.webCheckoutStartParamsSanitizerService.sanitizeCheckoutParams(this.decodeParams(startCheckoutQueryParam));
      this.vendorType = this.getCheckoutVendorType(startCheckoutParams.productIdentifier.productId);
      return this.webCheckoutStartupRedirectionService.startCheckout(navAction => this.navigate(navAction), startCheckoutParams, extras);
    } else if (signInQueryParam) {
      const signInParams = this.webCheckoutStartParamsSanitizerService.sanitizeSignInParams(this.decodeParams(signInQueryParam));
      this.vendorType = 'EXTERNAL';
      return this.webCheckoutStartupRedirectionService.signIn(navAction => this.navigate(navAction), signInParams, extras);
    } else if (startAcademicRegistrationQueryParam) {
      const startAcademicRegistrationParams = this.webCheckoutStartParamsSanitizerService.sanitizeAcademicParams(
        this.decodeParams(startAcademicRegistrationQueryParam)
      );
      this.vendorType = this.getCheckoutVendorType(startAcademicRegistrationParams.productIdentifier.productId);
      return this.webCheckoutStartupRedirectionService.startAcademicRegistration(
        navAction => this.navigate(navAction),
        startAcademicRegistrationParams,
        extras
      );
    }
    return throwError(() => new InvalidStateError('No query parameter found'));
  }

  private decodeParams<T>(base64: string): T {
    return JSON.parse(Buffer.from(base64, 'base64').toString());
  }

  private navigate(navigationAction: unknown): Observable<unknown> {
    return this.store.dispatch(new SetGuardsActivated(true)).pipe(switchMap(() => this.store.dispatch(navigationAction)));
  }

  private getCheckoutVendorType(stringWithProductId: string): CheckoutVendorType {
    if (stringWithProductId.includes('de.yatta.umllab')) {
      return 'UML_LAB';
    } else if (stringWithProductId.includes('de.yatta.')) {
      return 'YATTA';
    }
    return 'EXTERNAL';
  }

  getVendorType(): CheckoutVendorType {
    return this.vendorType;
  }
}
